import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { MagnifyingGlass, Leaf, CurrencyDollar, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import SupplierSpecsPanel from "@/components/SupplierSpecsPanel";
import DataState from "@/components/DataState";

const CAT_LABELS = {
  all: "All categories", herb: "Herbs", spice: "Spices",
  yeast_extract: "Yeast Extracts & HVP", natural_flavor: "Natural Flavors",
  filler: "Fillers & Bulking Agents",
  egg: "Eggs", acidulant: "Acidulants", starch: "Starches",
  asian: "Asian / Global", dairy: "Dairy & Cheese", broth_base: "Broth Bases",
  preservative: "Preservatives",
  custom: "My Custom",
};
const CAT_ORDER = [
  "all", "herb", "spice", "yeast_extract", "natural_flavor",
  "egg", "acidulant", "starch", "asian", "dairy", "broth_base",
  "preservative", "filler", "custom",
];
const CREATABLE_CATS = ["custom", "herb", "spice", "yeast_extract", "natural_flavor", "filler"];
const ALLERGENS = ["milk", "eggs", "fish", "shellfish", "tree_nuts", "peanuts", "wheat", "soybeans", "sesame"];
const ALLERGEN_LABEL = {
  milk: "Milk", eggs: "Eggs", fish: "Fish", shellfish: "Shellfish",
  tree_nuts: "Tree Nuts", peanuts: "Peanuts", wheat: "Wheat",
  soybeans: "Soy", sesame: "Sesame",
};

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [priceDrafts, setPriceDrafts] = useState({}); // {ingredientId: localValue}
  const [roleDrafts, setRoleDrafts] = useState({}); // {ingredientId: localValue}
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const reload = () => {
    const params = {};
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    setLoading(true);
    setLoadError(null);
    api.get("/ingredients", { params })
      .then((r) => setItems(r.data))
      .catch((err) => setLoadError(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat]);

  const savePrice = async (id, val) => {
    const price = Number(val);
    if (isNaN(price) || price < 0) return toast.error("Invalid price");
    try {
      await api.put(`/ingredients/${id}/price`, { price_per_lb: price });
      setItems((curr) => curr.map((x) => (x.id === id ? { ...x, price_per_lb: price } : x)));
      setPriceDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      toast.success("Price saved");
    } catch (e) {
      console.error("Save price failed", e);
      toast.error("Save failed");
    }
  };

  const saveRole = async (id, val) => {
    const role = String(val || "").trim();
    try {
      await api.put(`/ingredients/${id}/functional-role`, { functional_role: role });
      setItems((curr) => curr.map((x) => (x.id === id ? { ...x, functional_role: role || null } : x)));
      setRoleDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      toast.success(role ? "Functional role saved" : "Functional role cleared");
    } catch (e) {
      console.error("Save functional role failed", e);
      toast.error("Save failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Database</div>
            <h1 className="text-4xl font-bold tracking-tight">Ingredient library</h1>
            <p className="text-stone-600 mt-2 max-w-2xl">Curated USDA-derived nutrient profiles for herbs, spices, yeast extracts, natural flavors and fillers — plus your own custom ingredients. Enter your <b>supplier cost ($/lb)</b> below to enable batch costing in the Formulation Builder.</p>
            <div className="mt-3 inline-flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-2xl text-[12px] text-amber-900" data-testid="price-edit-hint">
              <CurrencyDollar size={14} className="text-amber-700 shrink-0 mt-0.5" weight="bold" />
              <span><b>Prices shown are estimates</b> from small-MOQ wholesale tier (Frontier, Mountain Rose, Atlantic Spice). Click any <b>$/lb</b> field below to override with your real supplier quote — saves automatically on Enter or click-away.</span>
            </div>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-800 hover:bg-emerald-700 text-white shrink-0" data-testid="add-ingredient-button">
                <Plus size={16} weight="bold" className="mr-1.5" /> Add custom ingredient
              </Button>
            </DialogTrigger>
            <AddIngredientDialog onClose={() => setAddOpen(false)} onCreated={reload} />
          </Dialog>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ingredients…"
              className="pl-9 border-stone-300 focus-visible:ring-emerald-500" data-testid="ingredient-search-input"/>
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-[220px] border-stone-300" data-testid="category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CAT_ORDER.map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <datalist id="functional-role-suggestions">
          {["Umami backbone", "Sweetness & body", "Aroma", "Glutamates & brightness",
            "Lingering heat", "Pungency & finish", "Smoke & color", "Seasoning & flavor lift",
            "Body & mouthfeel", "Long finish", "Acidity & brightness", "Anti-caking (flow)"]
            .map((r) => <option key={r} value={r} />)}
        </datalist>
        <div className="text-xs text-stone-500 mt-3">{items.length} ingredients</div>
        <DataState
          loading={loading}
          error={loadError}
          onRetry={reload}
          loadingLabel="Loading ingredient library…"
          testId="ingredients-load"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {items.map((i) => {
            const draftVal = priceDrafts[i.id];
            const displayVal = draftVal !== undefined ? draftVal : (i.price_per_lb || "");
            return (
              <Card key={i.id} className="p-4 border-stone-200" data-testid={`ingredient-card-${i.id}`}>
                <div className="flex items-start justify-between">
                  <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-800 grid place-items-center">
                    <Leaf size={16} weight="duotone" />
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                    {CAT_LABELS[i.category] || i.category}
                  </div>
                </div>
                <div className="font-semibold mt-2">{i.name}</div>
                <div className="text-xs text-stone-500 mt-0.5">As declared: “{i.declaration}”</div>
                {i.nutrition_status === "awaiting_supplier" && (
                  <p className="text-xs text-amber-800 bg-amber-50 p-2 mt-2 rounded">
                    Generic estimate—R&amp;D only. Supplier nutrition, allergen status and label declaration need verification.
                    Nutrition-label calculations are unavailable.
                  </p>
                )}
                <div className="mt-2 flex items-center gap-2 bg-violet-50/60 border border-violet-200 rounded-md px-2 py-1.5">
                  <label className="text-[10px] uppercase tracking-wider text-violet-700 shrink-0" title="What this ingredient does in a formula">Role</label>
                  <Input
                    value={roleDrafts[i.id] !== undefined ? roleDrafts[i.id] : (i.functional_role || "")}
                    onChange={(e) => setRoleDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                    onBlur={(e) => {
                      if (roleDrafts[i.id] !== undefined && roleDrafts[i.id].trim() !== (i.functional_role || "")) {
                        saveRole(i.id, e.target.value);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    placeholder="e.g. Umami backbone"
                    list="functional-role-suggestions"
                    className="h-7 px-2 text-sm border-violet-200 focus-visible:ring-violet-400"
                    data-testid={`role-input-${i.id}`}
                  />
                </div>
                <div className="text-xs mt-3 grid grid-cols-3 gap-1">
                  <Stat label="kcal" value={i.nutrition_status === "awaiting_supplier" ? (i.nutrition_reference?.values?.calories ?? "Unknown") : i.nutrients_per_100g.calories} />
                  <Stat label="Fat g" value={i.nutrition_status === "awaiting_supplier" ? (i.nutrition_reference?.values?.total_fat_g ?? "Unknown") : i.nutrients_per_100g.total_fat_g} />
                  <Stat label="Na mg" value={i.nutrition_status === "awaiting_supplier" ? (i.nutrition_reference?.values?.sodium_mg ?? "Unknown") : i.nutrients_per_100g.sodium_mg} />
                </div>
                {i.nutrition_reference && (
                  <div className="text-xs mt-2 text-stone-600">
                    <div className="font-medium">Generic reference per 100 g</div>
                    <div className="grid grid-cols-2 gap-1 my-2">
                      <Stat label="Carbs g" value={i.nutrition_reference.values.total_carbs_g ?? "Unknown"} />
                      <Stat label="Fiber g" value={i.nutrition_reference.values.fiber_g ?? "Unknown"} />
                      <Stat label="Protein g" value={i.nutrition_reference.values.protein_g ?? "Unknown"} />
                      <Stat label="Sugars g" value={i.nutrition_reference.values.sugars_g ?? "Unknown"} />
                    </div>
                    <p>{i.nutrition_reference.note}</p>
                    <a href={i.nutrition_reference.url} target="_blank" rel="noopener noreferrer" className="underline">Reference source</a>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-md px-2 py-1.5">
                  <CurrencyDollar size={14} className="text-emerald-700 shrink-0" />
                  <label className="text-[10px] uppercase tracking-wider text-stone-500 shrink-0">$/lb</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={displayVal}
                    onChange={(e) => setPriceDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                    onBlur={(e) => {
                      if (draftVal !== undefined && Number(draftVal) !== Number(i.price_per_lb || 0)) {
                        savePrice(i.id, e.target.value);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    placeholder="0.00"
                    className="h-7 px-2 text-sm border-stone-300 focus-visible:ring-emerald-500"
                    data-testid={`price-input-${i.id}`}
                  />
                </div>

                {(i.allergens.length > 0 || i.may_contain.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {i.allergens.map((a) => <Chip key={a} tone="red">{a.replace(/_/g, " ")}</Chip>)}
                    {i.may_contain.map((a) => <Chip key={"mc-" + a} tone="amber">{`may contain ${a.replace(/_/g, " ")}`}</Chip>)}
                  </div>
                )}

                <SupplierSpecsPanel ingredientId={i.id} />
              </Card>
            );
          })}
          </div>
        </DataState>
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-stone-50 rounded px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
function Chip({ tone, children }) {
  const cls = tone === "red" ? "bg-red-50 text-red-700 border-red-200" :
    tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" :
    "bg-stone-50 text-stone-700 border-stone-200";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>;
}

function AddIngredientDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", declaration: "", category: "custom", functional_role: "",
    price_per_lb: "",
    calories: "", total_fat_g: "", saturated_fat_g: "", sodium_mg: "",
    total_carbs_g: "", fiber_g: "", protein_g: "",
    allergens: [], may_contain: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const toggleAllergen = (key, list) => {
    const next = form[list].includes(key)
      ? form[list].filter((x) => x !== key)
      : [...form[list], key];
    setForm({ ...form, [list]: next });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Ingredient name is required");
    setSubmitting(true);
    try {
      const num = (v) => (v === "" || v === null ? 0 : Number(v));
      const body = {
        name: form.name.trim(),
        category: form.category,
        declaration: (form.declaration || form.name).trim(),
        functional_role: form.functional_role.trim() || null,
        allergens: form.allergens,
        may_contain: form.may_contain,
        nutrients_per_100g: {
          calories: num(form.calories),
          total_fat_g: num(form.total_fat_g),
          saturated_fat_g: num(form.saturated_fat_g),
          trans_fat_g: 0,
          cholesterol_mg: 0,
          sodium_mg: num(form.sodium_mg),
          total_carbs_g: num(form.total_carbs_g),
          fiber_g: num(form.fiber_g),
          sugars_g: 0,
          added_sugars_g: 0,
          protein_g: num(form.protein_g),
          vitamin_d_mcg: 0,
          calcium_mg: 0,
          iron_mg: 0,
          potassium_mg: 0,
        },
      };
      const resp = await api.post("/ingredients", body);
      // Optionally save price
      if (form.price_per_lb && Number(form.price_per_lb) > 0) {
        try {
          await api.put(`/ingredients/${resp.data.id}/price`, { price_per_lb: Number(form.price_per_lb) });
        } catch (err) { console.error("Set initial price failed", err); }
      }
      toast.success(`Added "${form.name}"`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      console.error("Create ingredient failed", err);
      toast.error(err?.response?.data?.detail || "Could not create ingredient");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-ingredient-dialog">
      <DialogHeader>
        <DialogTitle className="text-2xl">Add custom ingredient</DialogTitle>
        <p className="text-sm text-stone-600 mt-1">
          Add raw materials your supplier carries that aren't in the seed database. Nutrient values
          are per 100&nbsp;g — leave blank or 0 if you'll fill them in later from your supplier's spec sheet.
        </p>
      </DialogHeader>

      <div className="space-y-5 py-2">
        <section>
          <SectionTitle>Identity</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Ingredient name (internal)" required>
              <Input value={form.name} onChange={set("name")}
                placeholder="e.g., Smoked Paprika (Hungarian)"
                data-testid="add-ing-name" />
            </Field>
            <Field label="As declared on label">
              <Input value={form.declaration} onChange={set("declaration")}
                placeholder="e.g., Smoked Paprika (defaults to name)"
                data-testid="add-ing-declaration" />
            </Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="add-ing-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CREATABLE_CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Supplier cost ($/lb) — optional">
              <Input type="number" step="0.01" min="0"
                value={form.price_per_lb} onChange={set("price_per_lb")}
                placeholder="0.00"
                data-testid="add-ing-price" />
            </Field>
            <Field label="Functional role — optional">
              <Input value={form.functional_role} onChange={set("functional_role")}
                placeholder="e.g. Umami backbone, Smoke & color"
                list="functional-role-suggestions"
                data-testid="add-ing-role" />
            </Field>
          </div>
        </section>

        <section>
          <SectionTitle>Nutrients per 100&nbsp;g (optional, fill later if needed)</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Calories"><Input type="number" step="0.1" value={form.calories} onChange={set("calories")} placeholder="0" data-testid="add-ing-cal" /></Field>
            <Field label="Sodium (mg)"><Input type="number" step="0.1" value={form.sodium_mg} onChange={set("sodium_mg")} placeholder="0" data-testid="add-ing-sodium" /></Field>
            <Field label="Protein (g)"><Input type="number" step="0.1" value={form.protein_g} onChange={set("protein_g")} placeholder="0" data-testid="add-ing-protein" /></Field>
            <Field label="Total Fat (g)"><Input type="number" step="0.1" value={form.total_fat_g} onChange={set("total_fat_g")} placeholder="0" data-testid="add-ing-fat" /></Field>
            <Field label="Sat Fat (g)"><Input type="number" step="0.1" value={form.saturated_fat_g} onChange={set("saturated_fat_g")} placeholder="0" data-testid="add-ing-satfat" /></Field>
            <Field label="Total Carbs (g)"><Input type="number" step="0.1" value={form.total_carbs_g} onChange={set("total_carbs_g")} placeholder="0" data-testid="add-ing-carbs" /></Field>
            <Field label="Fiber (g)"><Input type="number" step="0.1" value={form.fiber_g} onChange={set("fiber_g")} placeholder="0" data-testid="add-ing-fiber" /></Field>
          </div>
        </section>

        <section>
          <SectionTitle>Allergens (FALCPA + FASTER Act)</SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {ALLERGENS.map((a) => (
              <label key={a} className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-stone-200 cursor-pointer hover:border-emerald-700"
                data-testid={`add-ing-allergen-${a}`}>
                <input type="checkbox" checked={form.allergens.includes(a)}
                  onChange={() => toggleAllergen(a, "allergens")}
                  className="accent-emerald-800" />
                <span>{ALLERGEN_LABEL[a]}</span>
              </label>
            ))}
          </div>
          <div className="text-[11px] text-stone-500 mt-2">
            Tick allergens that this ingredient <b>contains</b>. Cross-contact (may contain) can be added later.
          </div>
        </section>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="add-ing-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting || !form.name.trim()}
          className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="add-ing-submit">
          {submitting ? "Adding…" : "Add ingredient"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2 mt-1">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-stone-500">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
